import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable max-lines */
import { forwardRef, useCallback, useMemo, useState, } from 'react';
import clsx from 'clsx';
import Box from '@mui/material/Box';
import Grow from '@mui/material/Grow';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import usePriceFormatter from '../../hooks/usePriceFormatter';
import { usePlatformConfig } from '../../hooks/usePlatformConfig';
import { Translation } from '../Translation';
import useInputLabelDisplay from '../../hooks/useInputLabelDisplay';
import priceInputStyles from './priceInput.styles';
// TODO fix react hook linting error exhaustive-deps
/* eslint-disable react-hooks/exhaustive-deps */
const PriceInput = forwardRef(({ index, value, maxValue, minValue, label = '', name, message, error, hideZero, className, disabled, classes, required, 'data-test': dataTest, onChange, allowDecimal, usePeriodAsThousandsSeparator, ...props }, ref) => {
    const styles = priceInputStyles({});
    const [isInputFocus, setIsInputFocus] = useState(false);
    const { platformConfig: { priceDisplay: { primary: { currencyFormatConfig, currency }, }, salesEnablement, }, } = usePlatformConfig();
    const { hideZeroIfFocus } = salesEnablement;
    const priceFormatter = usePriceFormatter({
        currency,
        decimals: 0,
        currencyFormatConfig,
    });
    const { getLabel } = useInputLabelDisplay();
    const displayedLabel = getLabel(label);
    const handleChange = useCallback((event) => {
        event.preventDefault();
        if (!onChange) {
            return;
        }
        const numericString = event.target.value || '0';
        const replaceInnerDashesAndStartingZero = /(?:((?!^-)(?!\d+)(-))|(^0)(?=(?:\d|-)))/g;
        const replaceNonNumbericChars = allowDecimal ? /[^\d.-]/g : /[^\d-]/g;
        const newValue = numericString
            .replace(replaceInnerDashesAndStartingZero, '')
            .replace(replaceNonNumbericChars, '');
        if (minValue !== undefined) {
            if (newValue === '-' && minValue < 0) {
                event.target.value = newValue;
                return onChange(event);
            }
            if (newValue.startsWith('-') && minValue >= 0) {
                event.target.value = newValue.replace(/-/g, '0');
                return onChange(event);
            }
        }
        if ((minValue === undefined || parseFloat(newValue) >= minValue) &&
            (maxValue === undefined || parseFloat(newValue) <= maxValue)) {
            event.target.value = newValue;
            return onChange(event);
        }
    }, [onChange, minValue, maxValue]);
    const displayedValue = useMemo(() => {
        if (hideZero && Number(value) === 0) {
            return '';
        }
        if (value === '-') {
            return value;
        }
        if (Number.isNaN(Number(value))) {
            return '';
        }
        const parts = String(value).split('.');
        const formattedInteger = usePeriodAsThousandsSeparator
            ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')
            : parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        if (String(value).includes('.')) {
            return `${formattedInteger}.${parts[1].slice(0, 2) || ''}`;
        }
        if (hideZeroIfFocus && value == 0 && isInputFocus) {
            return formattedInteger.substring(1);
        }
        return formattedInteger;
    }, [hideZero, value, isInputFocus]);
    return (_jsxs(Box, { className: clsx(styles.root, className, classes?.root), children: [label && (_jsx(Typography, { variant: "subtitle1", children: _jsx(InputLabel, { id: `${index}-${label}`, className: clsx([styles.label, classes?.label]), classes: {
                        asterisk: clsx([styles.asterisk, classes?.asterisk]),
                    }, required: required, "data-test": `${dataTest}:priceInput:label`, children: displayedLabel }, void 0) }, void 0)), _jsx(TextField, { ...props, name: name, error: error, inputRef: ref, required: required, disabled: disabled, inputMode: "numeric", variant: "outlined", className: clsx([
                    styles.textField,
                    classes?.textField,
                    {
                        [styles.error]: error,
                    },
                    {
                        ...(classes?.error && {
                            [classes.error]: error,
                        }),
                    },
                ]), value: displayedValue, onChange: handleChange, ...(hideZeroIfFocus && {
                    onFocus: () => setIsInputFocus(true),
                    onBlur: () => setIsInputFocus(false),
                }), inputProps: {
                    max: maxValue ?? Infinity,
                    min: minValue ?? -Infinity,
                    step: 1,
                }, InputProps: {
                    startAdornment: (_jsx(InputAdornment, { position: "start", children: priceFormatter.format(0).replace('0', '') }, void 0)),
                    classes: {
                        focused: clsx([styles.inputFocused, classes?.inputFocused]),
                        root: clsx([
                            styles.inputRoot,
                            classes?.inputRoot,
                            {
                                [styles.disabled]: disabled,
                            },
                            {
                                ...(classes?.disabled && {
                                    [classes.disabled]: disabled,
                                }),
                            },
                        ]),
                        input: clsx([styles.input, classes?.input]),
                    },
                }, "data-test": `${dataTest}:priceInput` }, void 0), _jsx(Box, { className: clsx([styles.errorWrapper, classes?.errorWrapper]), children: error && message && (_jsx(Grow, { in: error, easing: ".1s", children: _jsx(FormHelperText, { className: clsx([styles.helper, classes?.helper]), "data-test": `${dataTest}:priceInput:error`, children: _jsx(Translation, { id: message }, void 0) }, void 0) }, void 0)) }, void 0)] }, void 0));
});
export default PriceInput;
//# sourceMappingURL=PriceInput.js.map